import { createSelector } from 'reselect';
import {
  assoc,
  append,
  path,
  prop,
  pathOr,
  pipe,
  propOr,
  filter,
  propEq,
  map,
  find,
  pathEq,
  pick,
  identity,
  mergeLeft,
  reject,
  concat,
  equals,
  head,
  includes,
  propSatisfies,
  applySpec,
  join,
  replace,
  uniq,
  reduce,
  when,
  ifElse,
} from 'ramda';
import moment from 'moment';

import { NA } from 'constants/common';
import { formatDate, formatDateOr, getAgeFromDobOr } from 'utilities/formatters';
import { PATIENT } from 'constants/resourceType';
import { FORM_NAMES } from 'constants/forms';
import { FHIR_LANGUAGE_VALUES } from 'constants/languages';
import i18n from 'services/i18n';
import { getUserRegion } from '../user';
import { getAllPractitioners } from '../organization';
import { getPatientPageUrlId } from '../router';

const localState = prop('patients');

export const getPatientsLoadingStatus = createSelector(
  localState,
  prop('loading'),
);

export const getEditPatient = createSelector(
  localState,
  prop('editPatient'),
);

export const getPatientsHiddenLoadingStatus = createSelector(
  getPatientsLoadingStatus,
  getEditPatient,
  (loading, data) => loading && !data,
);

export const getOrganization = createSelector(
  getUserRegion,
  region => ({
    reference: `Organization/${region}-organization`,
  }),
);

export const getPatientsData = createSelector(
  prop('patients'),
  identity,
);

export const getJoinedIdsFromPatientsList = pipe(
  map(prop('id')),
  join(','),
);

const getPatientsIdWithWarning = pipe(
  propOr([], 'entry'),
  map(
    pipe(
      path(['resource', 'subject', 'reference']),
      replace('Patient/', ''),
    ),
  ),
  uniq,
);

export const getPatientsListWithWarning = dependentData => {
  const patientsId = getPatientsIdWithWarning(dependentData);

  return map(
    when(propSatisfies(item => includes(item, patientsId), 'id'), assoc('withNotification', true)),
  );
};

export const getTransmissionReportsForPatientsList = pipe(
  propOr([], 'entry'),
  reduce((acc, report) => {
    const patientId = pipe(
      path(['resource', 'subject', 'reference']),
      replace('Patient/', ''),
    )(report);

    const getReportId = path(['resource', 'id']);
    const getlastUpdated = pipe(
      path(['resource', 'meta', 'lastUpdated']),
      formatDate('DD/MM/YY, hh:mm'),
    );

    return {
      ...acc,
      [patientId]: applySpec({
        id: getReportId,
        lastUpdated: getlastUpdated,
      })(report),
    };
  }, {}),
);

export const getPatientsListWithReport = dependentData => {
  const reports = getTransmissionReportsForPatientsList(dependentData);

  return map(
    ifElse(
      ({ id }) => reports[id],
      patient => assoc('report', reports[patient.id])(patient),
      assoc('report', null),
    ),
  );
};

export const getPatientsList = pipe(
  propOr([], 'entry'),
  map(
    pipe(
      propOr({}, 'resource'),
      applySpec({
        age: pipe(
          prop('birthDate'),
          date => getAgeFromDobOr(NA, date),
        ),
        activationDate: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'created-on')),
          prop('valueDateTime'),
          date => formatDateOr(NA, i18n.t('dateTimeFormat.activationDate'))(date),
        ),
        deviceVendor: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'device.name')),
          propOr(NA, 'valueCode'),
        ),
        deviceType: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'device.type')),
          propOr('', 'valueCode'),
        ),
        deceasedDate: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'dodeath')),
          prop('valueDate'),
          date =>
            date &&
            `Deceased: ${formatDateOr(null, i18n.t('dateTimeFormat.activationDate'))(date)}`,
        ),
        dthCode: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'dth_code')),
          propOr('', 'valueCode'),
        ),
        eightTeenMonthFollowup: pipe(
          propOr([], 'extension'),
          find(propEq('url', '18_month_followup')),
          propOr('', 'valueCode'),
          followupComplete =>
            followupComplete &&
            `${i18n.t('patient.eightTeenMonthVisit')}: ${
              followupComplete === 'Yes' ? i18n.t('complete') : i18n.t('incomplete')
            }`,
        ),
        name: pipe(
          propOr([], 'name'),
          head,
          propOr(NA, 'text'),
        ),
        id: prop('id'),
      }),
    ),
  ),
);

export const getPatientSearchData = createSelector(
  pathOr({}, ['form', FORM_NAMES.filterPatients, 'values']),
  pick(['searchValue', 'type']),
);

export const getPatientListParams = createSelector(
  getPatientsData,
  ({ pagination, ordering }) => ({ ...pagination, ...ordering }),
);

export const getCardiovascularInvestigations = createSelector(
  prop('cardiovascularInvestigations'),
  identity,
);

export const getPatientCardiovascularInvestigationsData = createSelector(
  getPatientPageUrlId,
  getCardiovascularInvestigations,
  (id, { loading, error, data }) => ({ loading, data: propOr([], id, data), error }),
);

export const getCreatedPatientData = createSelector(
  path(['form', FORM_NAMES.addPatient, 'values']),
  getOrganization,
  getAllPractitioners,
  (
    {
      patientId: id,
      firstName,
      lastName: family,
      patientPhone,
      email,
      dobMonth,
      dobYear,
      password,
      caregiverFirstName,
      caregiverLastName,
      caregiverPhone,
      alternateFirstName,
      alternateLastName,
      alternatePhone,
      language,
      checkboxContactVia,
      checkboxRecieve,
    } = {},
    organization,
    practitioners,
  ) => ({
    fhirModel: {
      resourceType: PATIENT,
      id,
      name: [
        {
          use: 'usual',
          text: `${firstName} ${family}`,
          family,
          given: [firstName],
        },
      ],
      extension: [
        {
          url: 'contact.viaSms',
          valueBoolean: !!checkboxContactVia,
        },
        {
          url: 'contact.emailNotifications',
          valueBoolean: !!checkboxRecieve,
        },
        {
          url: 'patient-id',
          valueString: id,
        },
        {
          url: 'created-on',
          valueDateTime: moment().format('YYYY-MM-DDThh:mm:ss.000Z'),
        },
      ],
      birthDate: `${dobYear}-${dobMonth}`,
      telecom: pipe(
        map(({ phone: value, type: use }) => ({ system: 'phone', value, use })),
        list =>
          email
            ? append(
                {
                  system: 'email',
                  value: email,
                  rank: 1,
                },
                list,
              )
            : list,
      )(patientPhone),
      contact: [
        {
          relationship: [
            {
              coding: [
                {
                  code: 'C',
                  display: 'patientsCaregiverInformation',
                },
              ],
              text: 'Caregiver information',
            },
          ],
          name: {
            use: 'usual',
            text: `${caregiverFirstName} ${caregiverLastName}`,
            family: caregiverLastName,
            given: [caregiverFirstName],
          },
          telecom: [
            ...caregiverPhone.map(({ phone: value, type: use }) => ({
              system: 'phone',
              value,
              use,
            })),
          ],
        },
        {
          relationship: [
            {
              coding: [
                {
                  code: 'O',
                  display: 'patientsAlternativeContactInformation',
                },
              ],
              text: 'Alternative emergency contact information',
            },
          ],
          name: {
            use: 'usual',
            text: `${alternateFirstName} ${alternateLastName}`,
            family: alternateLastName,
            given: [alternateFirstName],
          },
          telecom: [
            ...alternatePhone.map(({ phone: value, type: use }) => ({
              system: 'phone',
              value,
              use,
            })),
          ],
        },
      ],
      communication: [
        {
          language: FHIR_LANGUAGE_VALUES[language],
        },
      ],
      managingOrganization: organization,
      generalPractitioner: practitioners,
    },
    smileModel: {
      username: id,
      password,
    },
  }),
);

export const getEditPatientsDob = createSelector(
  getEditPatient,
  propOr(null, 'birthDate'),
);

export const getEditPatientsCaregiverInformation = createSelector(
  getEditPatient,
  pipe(
    propOr([], 'contact'),
    find(pathEq(['relationship', '0', 'text'], 'Caregiver information')),
  ),
);

export const getEditPatientsAlternateContactInformation = createSelector(
  getEditPatient,
  pipe(
    propOr([], 'contact'),
    find(pathEq(['relationship', '0', 'text'], 'Alternative emergency contact information')),
  ),
);

export const getEditPatientsFormInitialValues = createSelector(
  getEditPatient,
  getEditPatientsCaregiverInformation,
  getEditPatientsAlternateContactInformation,
  (data, caregiverInformation, alternateContactInformation) => ({
    firstName: path(['name', '0', 'given', '0'])(data),
    lastName: path(['name', '0', 'family'])(data),
    language: path(['communication', '0', 'language', 'coding', '0', 'code'])(data),
    patientPhone: pipe(
      propOr([], 'telecom'),
      filter(propEq('system', 'phone')),
      map(({ value, use }) => ({
        phone: value,
        type: use,
      })),
    )(data),
    email: pipe(
      propOr([], 'telecom'),
      find(propEq('system', 'email')),
      propOr('', 'value'),
    )(data),
    caregiverFirstName: path(['name', 'given', '0'])(caregiverInformation),
    caregiverLastName: path(['name', 'family'])(caregiverInformation),
    caregiverPhone: pipe(
      propOr([], 'telecom'),
      map(({ value, use }) => ({
        phone: value,
        type: use,
      })),
    )(caregiverInformation),
    alternateFirstName: path(['name', 'given', '0'])(alternateContactInformation),
    alternateLastName: path(['name', 'family'])(alternateContactInformation),
    alternatePhone: pipe(
      propOr([], 'telecom'),
      map(({ value, use }) => ({
        phone: value,
        type: use,
      })),
    )(alternateContactInformation),
    checkboxRecieve: pipe(
      propOr([], 'extension'),
      find(propEq('url', 'contact.emailNotifications')),
      prop('valueBoolean'),
      value => !!value,
    )(data),
    checkboxContactVia: pipe(
      propOr([], 'extension'),
      find(propEq('url', 'contact.viaSms')),
      prop('valueBoolean'),
      value => !!value,
    )(data),
  }),
);

export const getEditedPatientData = createSelector(
  path(['form', FORM_NAMES.editPatient, 'values']),
  getEditPatient,
  (
    {
      firstName,
      lastName: family,
      patientPhone,
      email,
      caregiverFirstName,
      caregiverLastName,
      caregiverPhone,
      alternateFirstName,
      alternateLastName,
      alternatePhone,
      language,
      checkboxContactVia,
      checkboxRecieve,
    } = {},
    editedPatient,
  ) =>
    mergeLeft(
      {
        name: [
          {
            use: 'usual',
            text: `${firstName} ${family}`,
            family,
            given: [firstName],
          },
        ],
        extension: pipe(
          propOr([], 'extension'),
          reject(
            ({ url }) => equals(url, 'contact.viaSms') || equals(url, 'contact.emailNotifications'),
          ),
          concat([
            {
              url: 'contact.viaSms',
              valueBoolean: checkboxContactVia,
            },
            {
              url: 'contact.emailNotifications',
              valueBoolean: checkboxRecieve,
            },
          ]),
        )(editedPatient),
        telecom: pipe(
          map(({ phone: value, type: use }) => ({ system: 'phone', value, use })),
          list =>
            email
              ? append(
                  {
                    system: 'email',
                    value: email,
                    rank: 1,
                  },
                  list,
                )
              : list,
        )(patientPhone),
        contact: [
          {
            relationship: [
              {
                coding: [
                  {
                    code: 'C',
                    display: 'patientsCaregiverInformation',
                  },
                ],
                text: 'Caregiver information',
              },
            ],
            name: {
              use: 'usual',
              text: `${caregiverFirstName} ${caregiverLastName}`,
              family: caregiverLastName,
              given: [caregiverFirstName],
            },
            telecom: [
              ...caregiverPhone.map(({ phone: value, type: use }) => ({
                system: 'phone',
                value,
                use,
              })),
            ],
          },
          {
            relationship: [
              {
                coding: [
                  {
                    code: 'O',
                    display: 'patientsAlternativeContactInformation',
                  },
                ],
                text: 'Alternative emergency contact information',
              },
            ],
            name: {
              use: 'usual',
              text: `${alternateFirstName} ${alternateLastName}`,
              family: alternateLastName,
              given: [alternateFirstName],
            },
            telecom: [
              ...alternatePhone.map(({ phone: value, type: use }) => ({
                system: 'phone',
                value,
                use,
              })),
            ],
          },
        ],
        communication: [
          {
            language: FHIR_LANGUAGE_VALUES[language],
          },
        ],
      },
      editedPatient,
    ),
);
